import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Avatar } from "../../components/Avatar.js";
import { FranchiseBadge } from "../../components/FranchiseBadge.js";
import { Button, inputStyle, Notice, Selector } from "../../components/ui.js";
import {
  amendSalePrice,
  auctionLotLabel,
  currentLot,
  lotProgress,
  moveSquadPlayer,
  placePlayer,
  toPlace,
} from "../../lib/auction.js";
import { type AuctionCurrency, formatPrice } from "../../lib/currency.js";
import type { FranchiseSetup } from "../../lib/franchises.js";
import type {
  AuctionPlayer,
  AuctionPoolState,
  AuctionSquad,
  Franchise,
  SquadFill,
  TournamentEvent,
} from "../../lib/types.js";

export const auctionStateKey = (eventId: string) => ["auction-state", eventId] as const;

/** Everything after a write: every pool's state, the tournament, and the public rosters. */
export function useRefresh(event: TournamentEvent) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["auction-state"] });
    void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
    void queryClient.invalidateQueries({ queryKey: ["rosters", event.tournamentId] });
  };
}

export interface Person {
  name: string;
  avatarUrl: string | null;
}

/**
 * Who everybody on an auction screen is: players from the pool's entries, captains from the
 * squads, franchises from the setup. Faces come along, because a room recognises a face before
 * it reads a name.
 */
export function usePeople(
  event: TournamentEvent,
  setup: FranchiseSetup,
  currency: AuctionCurrency,
) {
  const people = new Map<string, Person>(
    event.entries.map((e) => [e.memberId, { name: e.displayName, avatarUrl: e.avatarUrl }]),
  );
  for (const squad of setup.squads) {
    if (squad.captain && !people.has(squad.captain.id)) {
      people.set(squad.captain.id, {
        name: squad.captain.displayName,
        avatarUrl: squad.captain.avatarUrl,
      });
    }
  }
  const franchises = new Map(setup.franchises.map((f) => [f.id, f]));
  return {
    person: (id: string): Person => people.get(id) ?? { name: "Someone", avatarUrl: null },
    franchise: (franchiseId: string): Franchise | undefined => franchises.get(franchiseId),
    franchiseName: (franchiseId: string) => franchises.get(franchiseId)?.name ?? "A team",
    /** A price in the tournament's currency: "45 Cr". */
    price: (amount: number) => formatPrice(amount, currency),
  };
}

export type People = ReturnType<typeof usePeople>;

/** The pool's lots in order: which the room is on, and how much of each is taken. */
export function LotStrip({ state, people }: { state: AuctionPoolState; people: People }) {
  const current = currentLot(state);
  if (state.lots.length === 0) return null;
  return (
    <ol className="mb-5 flex gap-2 overflow-x-auto pb-1" aria-label="Lots">
      {state.lots.map((lot) => {
        const { taken, size } = lotProgress(state, lot.lotId);
        const on = current?.lotId === lot.lotId;
        const done = taken >= size && !on;
        return (
          <li
            key={lot.lotId}
            aria-current={on ? "step" : undefined}
            className="min-w-[112px] shrink-0 border-2 px-3 py-2"
            style={{
              borderColor: on ? "var(--color-line)" : "var(--color-line-muted)",
              opacity: done ? 0.55 : 1,
            }}
          >
            <p className="text-sm font-bold">{auctionLotLabel(lot)}</p>
            <p className="font-mono text-[11px] text-ink-muted">
              base {people.price(lot.basePrice ?? 0)} · {lot.share} each
            </p>
            <div
              className="mt-1.5 h-1"
              style={{ background: "var(--color-line-muted)" }}
              aria-label={`${taken} of ${size} taken`}
            >
              <div
                className="h-full"
                style={{
                  width: `${size ? Math.min(100, (taken / size) * 100) : 0}%`,
                  background: "var(--color-line)",
                }}
              />
            </div>
            <p className="mt-1 font-mono text-[11px]">
              {taken} / {size}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

/** A franchise's mark and name together, with its colour as a rule along the top. */
export function FranchiseHeading({
  franchise,
  name,
  size = 36,
  aside,
  wrap = false,
}: {
  franchise: Franchise | undefined;
  name: string;
  size?: number;
  aside?: React.ReactNode;
  /** Let a long name run to two lines instead of cutting it off, where the space is narrow. */
  wrap?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5">
      {franchise ? <FranchiseBadge franchise={franchise} size={size} /> : null}
      <span className={`min-w-0 flex-1 font-bold leading-tight ${wrap ? "" : "truncate"}`}>
        {name}
      </span>
      {aside}
    </div>
  );
}

/**
 * Each franchise's squad so far: its captain, then its players with their faces and, for the
 * organiser, what was paid. `editable` (after Finish) puts a team picker beside each player;
 * `amendable` (while the pool runs) an Amend beside each sale.
 */
export function TeamCards({
  event,
  state,
  people,
  editable = false,
  amendable = false,
}: {
  event: TournamentEvent;
  state: AuctionPoolState;
  people: People;
  editable?: boolean;
  amendable?: boolean;
}) {
  const refresh = useRefresh(event);
  const move = useMutation({
    mutationFn: (args: { memberId: string; squadId: string | null }) =>
      moveSquadPlayer(event.id, args.memberId, args.squadId),
    onSuccess: refresh,
  });
  const registered = new Set(
    event.entries.filter((e) => e.status === "registered").map((e) => e.memberId),
  );
  const captains = new Set(state.squads.flatMap((s) => (s.captainId ? [s.captainId] : [])));
  const options = [
    { value: "", label: "No team" },
    ...state.squads.map((squad) => ({
      value: squad.squadId,
      label: people.franchiseName(squad.franchiseId),
    })),
  ];
  const unplaced = editable
    ? state.players.filter(
        (p) => p.squadId === null && registered.has(p.memberId) && !captains.has(p.memberId),
      )
    : [];

  return (
    <div>
      {move.error ? <Notice tone="attention">{move.error.message}</Notice> : null}
      <div className="grid gap-4 sm:grid-cols-2">
        {state.squads.map((squad) => (
          <TeamCard
            key={squad.squadId}
            squad={squad}
            state={state}
            people={people}
            picker={
              editable
                ? (memberId: string) => (
                    <Selector
                      label={`Team for ${people.person(memberId).name}`}
                      value={squad.squadId}
                      onChange={(value) => move.mutate({ memberId, squadId: value || null })}
                      options={options}
                    />
                  )
                : undefined
            }
            amend={
              amendable
                ? (player: AuctionPlayer) => (
                    <AmendPrice event={event} player={player} people={people} />
                  )
                : undefined
            }
          />
        ))}
      </div>
      {unplaced.length > 0 ? (
        <div className="mt-6">
          <p className="micro mb-2">In no team</p>
          <ul>
            {unplaced.map((p) => (
              <li key={p.memberId} className="flex items-center gap-3 py-1.5 text-sm">
                <Avatar
                  name={people.person(p.memberId).name}
                  url={people.person(p.memberId).avatarUrl}
                />
                <span className="min-w-0 flex-1">{people.person(p.memberId).name}</span>
                <Selector
                  label={`Team for ${people.person(p.memberId).name}`}
                  value=""
                  onChange={(value) =>
                    value ? move.mutate({ memberId: p.memberId, squadId: value }) : undefined
                  }
                  options={options}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One franchise's squad. Read by organisers and, through their own view, by a pool's captains,
 * so it takes only what both have: no refusals.
 */
export function TeamCard({
  squad,
  state,
  people,
  picker,
  amend,
}: {
  squad: Omit<AuctionSquad, "refusal">;
  state: { fill: SquadFill; players: AuctionPlayer[] };
  people: People;
  picker?: (memberId: string) => React.ReactNode;
  amend?: (player: AuctionPlayer) => React.ReactNode;
}) {
  const franchise = people.franchise(squad.franchiseId);
  const players = state.players.filter((p) => p.squadId === squad.squadId);
  const captain = squad.captainId ? people.person(squad.captainId) : null;
  return (
    <section
      className="border-2 p-3"
      style={{
        borderColor: "var(--color-line-muted)",
        borderTop: `4px solid ${franchise?.colour ?? "var(--color-line)"}`,
      }}
      aria-label={`${people.franchiseName(squad.franchiseId)} team`}
    >
      <FranchiseHeading
        franchise={franchise}
        name={people.franchiseName(squad.franchiseId)}
        aside={
          state.fill === "auction" ? (
            <span className="font-mono text-[11px] text-ink-muted">
              {people.price(squad.left)} of {people.price(squad.purse)} left
            </span>
          ) : null
        }
      />
      <ul className="mt-3 text-sm">
        {captain ? (
          <li className="flex items-center gap-2.5 py-1">
            <Avatar name={captain.name} url={captain.avatarUrl} size={32} />
            <span className="min-w-0 flex-1 font-semibold">{captain.name}</span>
            <span className="micro">captain</span>
          </li>
        ) : null}
        {players.map((p) => {
          const who = people.person(p.memberId);
          return (
            <li key={p.memberId} className="flex flex-wrap items-center gap-x-2.5 gap-y-1 py-1">
              <Avatar name={who.name} url={who.avatarUrl} size={32} />
              <span className="min-w-0 flex-1">{who.name}</span>
              {p.price !== null ? (
                <span className="font-mono text-[12px] tabular-nums">{people.price(p.price)}</span>
              ) : null}
              {picker ? picker(p.memberId) : null}
              {amend && p.state === "sold" ? amend(p) : null}
            </li>
          );
        })}
        {players.length === 0 && !captain ? (
          <li className="py-1 text-ink-muted">Nobody yet</li>
        ) : null}
      </ul>
    </section>
  );
}

/**
 * Change what a sale cost while the pool runs. The player stays in their team; the database
 * moves the purse by the difference and refuses a price below the base or beyond the purse.
 */
function AmendPrice({
  event,
  player,
  people,
}: {
  event: TournamentEvent;
  player: AuctionPlayer;
  people: People;
}) {
  const refresh = useRefresh(event);
  const [value, setValue] = useState<string | null>(null);
  const amend = useMutation({
    mutationFn: (price: number) => amendSalePrice(event.id, player.memberId, price),
    onSuccess: () => {
      setValue(null);
      refresh();
    },
  });
  const name = people.person(player.memberId).name;

  if (value === null) {
    return (
      <button
        type="button"
        className="font-mono text-[11px] underline"
        onClick={() => setValue(String(player.price ?? ""))}
        aria-label={`Amend ${name}'s price`}
      >
        Amend
      </button>
    );
  }

  const price = Number(value);
  const valid = value.trim() !== "" && Number.isInteger(price) && price >= 0;
  return (
    <form
      className="flex w-full flex-wrap items-center justify-end gap-2"
      onSubmit={(submit) => {
        submit.preventDefault();
        if (valid) amend.mutate(price);
      }}
    >
      <input
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        value={value}
        onChange={(change) => setValue(change.target.value)}
        aria-label={`New price for ${name}`}
        style={{ ...inputStyle, width: 96 }}
        autoFocus
      />
      <Button type="submit" tone="line" disabled={!valid || amend.isPending}>
        Save
      </Button>
      <Button
        type="button"
        tone="quiet"
        onClick={() => {
          setValue(null);
          amend.reset();
        }}
      >
        Cancel
      </Button>
      {amend.error ? (
        <p role="alert" className="w-full text-right text-[12px] text-attention">
          {amend.error.message}
        </p>
      ) : null}
    </form>
  );
}

/** The players the rules hand to the organiser, each with a team to put them in. */
export function PlaceList({
  event,
  state,
  people,
}: {
  event: TournamentEvent;
  state: AuctionPoolState;
  people: People;
}) {
  const refresh = useRefresh(event);
  const players = toPlace(state);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const place = useMutation({
    mutationFn: (args: { memberId: string; squadId: string }) =>
      placePlayer(event.id, args.memberId, args.squadId),
    onSuccess: refresh,
  });

  if (players.length === 0) return null;

  const options = [
    { value: "", label: "Choose a team" },
    ...state.squads.map((squad) => ({
      value: squad.squadId,
      label:
        state.fill === "auction"
          ? `${people.franchiseName(squad.franchiseId)} · ${people.price(squad.left)} left`
          : people.franchiseName(squad.franchiseId),
    })),
  ];

  return (
    <section className="mb-6" aria-label="To place by you">
      <h2 className="micro mb-2">To place by you</h2>
      {place.error ? <Notice tone="attention">{place.error.message}</Notice> : null}
      <ul>
        {players.map((p) => {
          const who = people.person(p.memberId);
          const lot = state.lots.find((l) => l.lotId === p.lotId);
          const squadId = choice[p.memberId] ?? "";
          return (
            <li
              key={p.memberId}
              className="flex flex-wrap items-center gap-3 border-b py-2"
              style={{ borderColor: "var(--color-line-muted)" }}
            >
              <Avatar name={who.name} url={who.avatarUrl} size={36} />
              <span className="min-w-0 flex-1 font-bold">
                {who.name}
                {lot ? (
                  <span className="ml-2 font-mono text-[11px] font-normal text-ink-muted">
                    {auctionLotLabel(lot)} · {people.price(lot.basePrice ?? 0)}
                  </span>
                ) : null}
              </span>
              <Selector
                label={`Team for ${who.name}`}
                value={squadId}
                onChange={(value) => setChoice((all) => ({ ...all, [p.memberId]: value }))}
                options={options}
              />
              <Button
                tone="line"
                disabled={!squadId || place.isPending}
                onClick={() => place.mutate({ memberId: p.memberId, squadId })}
                aria-label={`Place ${who.name}`}
              >
                Place
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
